import { z } from 'zod';

/** Time zones offered for a workspace's reporting day (US-only in Phase 0). */
export const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Mountain, no DST (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
] as const;

export type UsTimezone = (typeof US_TIMEZONES)[number]['value'];

const TIMEZONE_VALUES = US_TIMEZONES.map((t) => t.value) as [UsTimezone, ...UsTimezone[]];
export const TimezoneSchema = z.enum(TIMEZONE_VALUES, { error: 'Choose a time zone' });

export const WorkspaceNameSchema = z
  .string()
  .trim()
  .min(1, 'Enter a workspace name')
  .max(120, 'Use at most 120 characters');

/** Matches the `workspaces.slug` check constraint. */
export const SlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, 'Use at least 2 characters')
  .max(48, 'Use at most 48 characters')
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Use lowercase letters, digits and single dashes');

/** Suggests a slug from a workspace name ("Acme Funding, LLC" → "acme-funding-llc"). */
export function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '');
}

/** The admin-editable part of a workspace (column grants in M1: name, timezone, default assignee). */
export const WorkspaceSettingsFormSchema = z.object({
  name: WorkspaceNameSchema,
  timezone: TimezoneSchema,
  defaultAssigneeUserId: z.uuid({ error: 'Choose a default assignee' }),
});
export type WorkspaceSettingsForm = z.infer<typeof WorkspaceSettingsFormSchema>;
